import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { errorId, useFieldErrors } from "@/lib/formErrors";
import { fmtDate } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { FieldError, invalidClass } from "@/components/FieldError";
import { FieldLabel } from "@/components/FieldLabel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import DocumentHeaderPreview from "@/components/settings/DocumentHeaderPreview";
import { toast } from "sonner";

const FIELDS = ["title", "doc_no", "rev_no", "effective_date", "footer_note"];
// The server's own limits, so a box stops where the server would refuse.
const MAX = { company_name: 80, title: 80, doc_no: 30, rev_no: 10, footer_note: 120 };

const fieldKey = (doc, field) => `${doc}.${field}`;

function toDraft(saved) {
  return {
    company_name: saved?.company_name ?? "",
    documents: Object.fromEntries((saved?.documents || []).map((d) => [
      d.key, Object.fromEntries(FIELDS.map((f) => [f, d[f] ?? ""])),
    ])),
  };
}

/** Only what differs from what is stored: the PUT is partial, and a field nobody touched must not be re-sent. */
function changesFrom(saved, draft) {
  const out = {};
  if (draft.company_name !== (saved.company_name ?? "")) out.company_name = draft.company_name;
  const documents = {};
  for (const d of saved.documents || []) {
    const mine = draft.documents[d.key] || {};
    const diff = Object.fromEntries(FIELDS.filter((f) => (mine[f] ?? "") !== (d[f] ?? "")).map((f) => [f, mine[f]]));
    if (Object.keys(diff).length) documents[d.key] = diff;
  }
  if (Object.keys(documents).length) out.documents = documents;
  return out;
}

// What the server would refuse anyway, said before the round trip and under the same box.
function problemsIn(draft, docs) {
  const out = {};
  if (!draft.company_name.trim()) out.company_name = "The company name prints at the top of every form, so it cannot be blank.";
  for (const d of docs) {
    const v = draft.documents[d.key] || {};
    if (!(v.title || "").trim()) out[fieldKey(d.key, "title")] = "Every form needs its title — it prints under the company name.";
    if ((v.footer_note || "").trim().split("\n").length > 2) {
      out[fieldKey(d.key, "footer_note")] = "Two lines at most — it prints in a small box.";
    }
  }
  return out;
}

const isShape = (v) => !!v && typeof v === "object" && typeof v.company_name === "string" && Array.isArray(v.documents);

// On a wide screen the preview stays pinned above the forms while HR scrolls through them. On a
// phone it sits inside the form being edited instead: pinned there, it would cover the very box
// the keyboard is typing into.
function useWide(query = "(min-width: 768px)") {
  const read = () => typeof window !== "undefined" && typeof window.matchMedia === "function"
    && window.matchMedia(query).matches;
  const [wide, setWide] = useState(read);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia(query);
    const on = () => setWide(mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, [query]);
  return wide;
}

/**
 * Document control for Shakun's own controlled forms — the block their auditor reads at the top of
 * every form: Doc. No., Rev. No., Effective date, plus the form's title and the small revision box
 * F-TRG-04 carries at its foot. HR revises their forms themselves, so these are theirs to edit; every
 * value is read when a document is made, never pinned to one already printed.
 */
export default function DocumentControl({ saved, writable, onSaved }) {
  const docs = useMemo(() => saved?.documents || [], [saved]);
  const [draft, setDraft] = useState(() => toDraft(saved));
  const [active, setActive] = useState(docs[0]?.key);
  const [saving, setSaving] = useState(false);
  const { errors, report, clear } = useFieldErrors();
  const wide = useWide();

  // A save, or the section loading again, is the new starting point.
  useEffect(() => {
    setDraft(toDraft(saved));
    clear();
    setActive((cur) => ((saved?.documents || []).some((d) => d.key === cur) ? cur : saved?.documents?.[0]?.key));
  }, [saved, clear]);

  const changes = useMemo(() => changesFrom(saved || {}, draft), [saved, draft]);
  const dirty = Object.keys(changes).length > 0;
  const known = useMemo(() => ["company_name", ...docs.flatMap((d) => FIELDS.map((f) => fieldKey(d.key, f)))], [docs]);

  const setCompany = (value) => {
    setDraft((cur) => ({ ...cur, company_name: value }));
    clear("company_name");
  };
  const setField = (doc, field, value) => {
    setDraft((cur) => ({ ...cur, documents: { ...cur.documents, [doc]: { ...cur.documents[doc], [field]: value } } }));
    clear(fieldKey(doc, field));
  };

  const save = async () => {
    if (!dirty) return;
    const problems = problemsIn(draft, docs);
    if (Object.keys(problems).length) {
      report({ fields: problems }, { known });
      return;
    }
    setSaving(true);
    try {
      const r = await api.put("/settings/documents", changes);
      // A 2xx is trusted only with the right shape: an HTML page where JSON belongs would otherwise
      // reset this form to nothing and call it saved.
      if (!isShape(r.data)) throw new Error("unexpected response");
      clear();
      onSaved(r.data);
      toast.success("Document control saved");
    } catch (e) {
      report(e, { fallback: "Document control could not be saved.", known });
    }
    setSaving(false);
  };

  const discard = () => {
    setDraft(toDraft(saved));
    clear();
  };

  const shownDoc = docs.find((d) => d.key === active) || docs[0];
  const preview = shownDoc && (
    <div>
      <div className="overline text-muted-foreground mb-1.5" data-testid="doc-preview-label">
        Preview · {shownDoc.name}
      </div>
      <DocumentHeaderPreview
        companyName={draft.company_name}
        title={draft.documents[shownDoc.key]?.title}
        docNo={draft.documents[shownDoc.key]?.doc_no}
        revNo={draft.documents[shownDoc.key]?.rev_no}
        effectiveDate={draft.documents[shownDoc.key]?.effective_date}
        footerNote={draft.documents[shownDoc.key]?.footer_note} />
    </div>
  );
  // Focus scrolling must leave a box clear of the pinned preview above it.
  const under = wide ? "scroll-mt-60" : "";

  return (
    <div className="space-y-5" data-testid="doc-control">
      <fieldset className="space-y-5 border-0 p-0 m-0 min-w-0" disabled={!writable}>
        <div className="max-w-xl">
          <FieldLabel htmlFor="doc-company" required>Company name</FieldLabel>
          <Input id="doc-company" value={draft.company_name} maxLength={MAX.company_name} autoComplete="off"
                 onChange={(e) => setCompany(e.target.value)}
                 className={cn(under, invalidClass(errors, "company_name"))}
                 data-field="company_name" aria-invalid={errors.company_name ? true : undefined}
                 aria-describedby={errors.company_name ? errorId("company_name") : "doc-company-hint"}
                 data-testid="doc-company" />
          <FieldError field="company_name" errors={errors} />
          <p id="doc-company-hint" className="text-xs text-muted-foreground mt-1">
            Printed at the top of every form, as your forms print it.
          </p>
        </div>

        {wide && (
          // bg-card, so the forms scroll underneath it rather than showing through.
          <div className="sticky top-0 z-10 -mx-1 bg-card px-1 pb-3 pt-1">{preview}</div>
        )}

        {docs.map((d) => {
          const v = draft.documents[d.key] || {};
          const id = (f) => `doc-${d.key}-${f}`;
          const box = (f) => ({
            id: id(f),
            "data-field": fieldKey(d.key, f),
            "aria-invalid": errors[fieldKey(d.key, f)] ? true : undefined,
            "data-testid": id(f),
          });
          const hint = (f, text) => (
            <p id={`${id(f)}-hint`} className="text-xs text-muted-foreground mt-1">{text}</p>
          );
          const described = (f) => (errors[fieldKey(d.key, f)] ? errorId(fieldKey(d.key, f)) : `${id(f)}-hint`);
          const errorOnly = (f) => (errors[fieldKey(d.key, f)] ? errorId(fieldKey(d.key, f)) : undefined);
          return (
            <section key={d.key} onFocusCapture={() => setActive(d.key)}
                     className={cn("rounded-md border p-4 space-y-3 min-w-0 transition-colors",
                                   shownDoc?.key === d.key ? "border-primary/50 bg-muted/20" : "border-border")}
                     aria-labelledby={id("name")} data-testid={`doc-${d.key}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                <h4 id={id("name")} className="font-medium">{d.name}</h4>
                <span className="font-mono text-xs text-muted-foreground" data-testid={`doc-${d.key}-badge`}>
                  {(v.doc_no || "").trim() || "No number"} · Rev {(v.rev_no || "").trim() || "—"}
                </span>
              </div>

              {!wide && shownDoc?.key === d.key && preview}

              {/* The date gets the widest short column: a date box needs ~140px for its text and its
                  picker icon, and at 768px beside the sidebar an even split clipped the icon. */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,0.7fr)_minmax(0,1.3fr)] lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,0.7fr)_minmax(0,1.3fr)]">
                <div className="min-w-0 sm:col-span-3 lg:col-span-1">
                  <FieldLabel htmlFor={id("title")} required>Title</FieldLabel>
                  <Input {...box("title")} value={v.title} maxLength={MAX.title} autoComplete="off"
                         onChange={(e) => setField(d.key, "title", e.target.value)}
                         aria-describedby={errorOnly("title")}
                         className={cn(under, invalidClass(errors, fieldKey(d.key, "title")))} />
                  <FieldError field={fieldKey(d.key, "title")} errors={errors} />
                </div>
                <div className="min-w-0">
                  <Label htmlFor={id("doc_no")}>Doc. No.</Label>
                  <Input {...box("doc_no")} value={v.doc_no} maxLength={MAX.doc_no} autoComplete="off" spellCheck={false}
                         onChange={(e) => setField(d.key, "doc_no", e.target.value)}
                         aria-describedby={errorOnly("doc_no")}
                         className={cn("font-mono", under, invalidClass(errors, fieldKey(d.key, "doc_no")))} />
                  <FieldError field={fieldKey(d.key, "doc_no")} errors={errors} />
                </div>
                <div className="min-w-0">
                  <Label htmlFor={id("rev_no")}>Rev. No.</Label>
                  <Input {...box("rev_no")} value={v.rev_no} maxLength={MAX.rev_no} autoComplete="off" spellCheck={false}
                         onChange={(e) => setField(d.key, "rev_no", e.target.value)}
                         aria-describedby={errorOnly("rev_no")}
                         className={cn("font-mono", under, invalidClass(errors, fieldKey(d.key, "rev_no")))} />
                  <FieldError field={fieldKey(d.key, "rev_no")} errors={errors} />
                </div>
                <div className="min-w-0">
                  <Label htmlFor={id("effective_date")}>Effective date</Label>
                  <Input {...box("effective_date")} type="date" value={v.effective_date}
                         onChange={(e) => setField(d.key, "effective_date", e.target.value)}
                         aria-describedby={described("effective_date")}
                         className={cn(under, invalidClass(errors, fieldKey(d.key, "effective_date")))} />
                  <FieldError field={fieldKey(d.key, "effective_date")} errors={errors} />
                  {hint("effective_date", v.effective_date ? `Prints as ${fmtDate(v.effective_date)}` : "Not set")}
                </div>
              </div>

              <div className="min-w-0">
                <Label htmlFor={id("footer_note")}>Footer box</Label>
                <Textarea {...box("footer_note")} rows={2} value={v.footer_note} maxLength={MAX.footer_note}
                          onChange={(e) => setField(d.key, "footer_note", e.target.value)}
                          aria-describedby={described("footer_note")}
                          className={cn("resize-none", under, invalidClass(errors, fieldKey(d.key, "footer_note")))} />
                <FieldError field={fieldKey(d.key, "footer_note")} errors={errors} />
                {hint("footer_note",
                      "Printed in a small box at the bottom left of every page — your form's own revision line. Leave blank for none.")}
              </div>
            </section>
          );
        })}

        {writable && (
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={save} disabled={saving || !dirty} data-testid="doc-save">
              {saving ? "Saving..." : "Save"}
            </Button>
            {dirty && !saving && (
              <>
                <Button type="button" variant="ghost" onClick={discard} data-testid="doc-discard">Discard changes</Button>
                <span className="text-xs text-muted-foreground" data-testid="doc-unsaved">Unsaved changes</span>
              </>
            )}
          </div>
        )}
      </fieldset>
    </div>
  );
}
